import { useCallback, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';

/**
 * useHostingSubscriptionInitiate — kicks off the PayFast Recurring setup
 * flow for a customer's hosting account.
 *
 * Spec: _bmad-output/PHASE_PF_Q3_REVERSAL_BRD_2026-06-16.md §5.1 + §5.2
 *
 * Pattern: mirrors usePayFastInitiate. Calls the hosting-subscription-initiate
 * EF, receives { action_url, fields }, builds a hidden form, auto-submits to
 * PayFast. PayFast captures the card + posts first cycle ITN to payfast-webhook
 * which stores the token + flips hosting_subscription.status to 'active'.
 *
 * The hosting_subscription row is INSERTed by the EF in 'pending' state BEFORE
 * the form is served (BRD R-1 mitigation). Customers can't double-initiate
 * because of the partial UNIQUE index on (customer_link_id) WHERE status IN
 * ('pending','active','at_risk').
 */

export interface HostingSubscriptionInitiateError {
  code: string;
  message: string;
}

export interface UseHostingSubscriptionInitiateResult {
  initiate: () => Promise<void>;
  isLoading: boolean;
  error: HostingSubscriptionInitiateError | null;
}

interface InitiateResponse {
  ok: true;
  hosting_subscription_id: string;
  action_url: string;
  fields: Record<string, string>;
}

export function useHostingSubscriptionInitiate(
  customerLinkId: string
): UseHostingSubscriptionInitiateResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<HostingSubscriptionInitiateError | null>(null);

  const initiate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: efError } = await supabase.functions.invoke<InitiateResponse>(
        'hosting-subscription-initiate',
        { body: { customer_link_id: customerLinkId } }
      );

      if (efError) {
        const code = await extractErrorCode(efError);
        setError({ code, message: friendlyErrorMessage(code) });
        setIsLoading(false);
        return;
      }

      if (!data?.ok) {
        setError({ code: 'unknown', message: friendlyErrorMessage('unknown') });
        setIsLoading(false);
        return;
      }

      // Auto-submit PayFast form (vanilla DOM, same pattern as usePayFastInitiate).
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.action_url;
      form.style.display = 'none';
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      // Browser navigates to PayFast; isLoading stays true through navigation.
    } catch (err) {
      setError({ code: 'unknown', message: (err as Error).message });
      setIsLoading(false);
    }
  }, [customerLinkId]);

  return { initiate, isLoading, error };
}

async function extractErrorCode(err: unknown): Promise<string> {
  try {
    const ctx = (err as { context?: { response?: Response } }).context;
    if (ctx?.response) {
      const body = await ctx.response.clone().json();
      if (typeof body?.code === 'string') return body.code;
    }
  } catch {
    /* ignore */
  }
  return 'unknown';
}

function friendlyErrorMessage(code: string): string {
  switch (code) {
    case 'already_subscribed':
      return 'This hosting account already has auto-pay configured.';
    case 'customer_link_not_found':
      return 'We could not find this hosting account.';
    case 'forbidden_not_owner':
      return "You don't have permission to set up auto-pay on this account.";
    case 'no_service_plan_linked':
      return 'This account has no plan attached. Contact support.';
    case 'plan_inactive':
      return 'This plan is no longer available. Contact support to migrate.';
    case 'plan_not_monthly':
      return 'Auto-pay only supports monthly billing right now.';
    case 'unauthenticated':
      return 'Please sign in again to continue.';
    default:
      return 'Could not start auto-pay setup. Please try again or contact support.';
  }
}
